import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AppSelect from '../../components/common/AppSelect';
import { useAuth } from '../../contexts/AuthContext';

function UsuariosFormPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    perfil_id: '',
    orgao_id: '',
  });
  const [perfis, setPerfis] = useState([]);
  const [orgaos, setOrgaos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Estados LDAP / verificação de e-mail
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [isInternalUser, setIsInternalUser] = useState(false);
  const [authTypeMessage, setAuthTypeMessage] = useState('');
  const [fieldsDisabled, setFieldsDisabled] = useState(true);
  const [userAlreadyExists, setUserAlreadyExists] = useState(false);
  const [originalEmail, setOriginalEmail] = useState('');

  const isEditing = Boolean(id);
  const isEditingSelf = isEditing && user && parseInt(id, 10) === user.id;
  const isEditingSuperAdmin = isEditing && parseInt(id, 10) === 1;

  const pageTitle = isEditing ? 'Editar Usuário' : 'Novo Usuário';
  const pageSubtitle = isEditing ? `Editando o registro #${id}` : 'Preencha os dados para criar um novo usuário';

  // Carrega perfis/órgãos e (se edição) dados do usuário
  useEffect(() => {
    if (authLoading) return;

    const fetchData = async () => {
      try {
        const [perfisResponse, orgaosResponse] = await Promise.all([
          api.get('/perfis', {
            params: {
              per_page: 10000,
              status: 'active',
            },
          }),
          api.get('/orgaos', {
            params: {
              per_page: 10000,
              status: 'active',
            },
          }),
        ]);
        setPerfis(perfisResponse.data?.data ?? perfisResponse.data);
        setOrgaos(orgaosResponse.data?.data ?? orgaosResponse.data);

        if (isEditing) {
          const usuarioResponse = await api.get(`/users/${id}`);
          const payload = usuarioResponse.data?.data ?? usuarioResponse.data;
          const { name, email, perfil_id, orgao_id } = payload;
          setFormData({ name, email, perfil_id, orgao_id: orgao_id || '' });
          setOriginalEmail(email);
          setIsInternalUser(email.toLowerCase().endsWith('@sejus.df.gov.br'));
          setFieldsDisabled(false);
          setEmailVerified(true);
        } else {
          // criação: começa bloqueado até verificar e-mail
          setFieldsDisabled(true);
        }
      } catch (err) {
        if (err.response?.status === 403) {
          setError('Acesso negado (403).');
        } else {
          setError('Não foi possível carregar os dados necessários.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authLoading, id, isEditing]);

  // Verificar email (LDAP ou existência no banco)
  const verifyEmail = async () => {
    if (!formData.email) {
      setError('Por favor, insira um email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Email inválido');
      return;
    }

    setVerifying(true);
    setError(null);
    setAuthTypeMessage('');
    setUserAlreadyExists(false);

    const normalizedEmail = formData.email.trim().toLowerCase();
    const emailChanged = normalizedEmail !== originalEmail.trim().toLowerCase();
    if (isEditing && !emailChanged) {
      setIsInternalUser(normalizedEmail.endsWith('@sejus.df.gov.br'));
      setEmailVerified(true);
      setVerifying(false);
      return;
    }

    if (!isEditing) {
      // Reset parcial dos campos (mantém e-mail)
      setFormData(prev => ({
        name: '',
        email: prev.email,
        perfil_id: '',
        orgao_id: ''
      }));
      setFieldsDisabled(false);
    }

    setEmailVerified(false);

    try {
      const isSejusEmail = formData.email.toLowerCase().endsWith('@sejus.df.gov.br');

      // 1) já existe?
      try {
        const checkResponse = await api.post('/check-user-exists', { email: formData.email });
        if (checkResponse.data.exists) {
          setUserAlreadyExists(true);
          setEmailVerified(true);
          if (checkResponse.data.is_soft_deleted) {
            setAuthTypeMessage('❌ Este email pertence a um usuário que foi desativado.');
          } else {
            setAuthTypeMessage('❌ Este email já está cadastrado no sistema.');
          }
          setFieldsDisabled(true);
          return;
        }
      } catch {
        // falhou? segue fluxo mesmo assim
      }

      // 2) fluxo por domínio
      if (isSejusEmail) {
        const response = await api.get(`/ldap/search/${encodeURIComponent(formData.email)}`);
        if (response.data.success) {
          const userData = response.data.data;
          setIsInternalUser(true);
          setEmailVerified(true);
          setAuthTypeMessage('✅ Usuário interno SEJUS encontrado no LDAP. Senha será validada pelo sistema corporativo.');
          setFormData(prev => (
            isEditing
              ? { ...prev, name: userData.name }
              // Preenche nome do LDAP, perfil padrão "Operador" (id=2) e órgão "SEJUS" (id=1)
              : { ...prev, name: userData.name, perfil_id: '2', orgao_id: '1' }
          ));
          setFieldsDisabled(false);
        } else {
          setError('Usuário não encontrado no diretório SEJUS. Verifique o email informado.');
          setFieldsDisabled(true);
        }
      } else {
        setIsInternalUser(false);
        setEmailVerified(true);
        setUserAlreadyExists(false);
        setAuthTypeMessage(
          isEditing
            ? '✅ Email disponível. O usuário será notificado após o salvamento.'
            : '✅ Email disponível. Uma senha será gerada automaticamente e enviada por email.'
        );
        setFieldsDisabled(false);
        if (!isEditing) {
          setFormData(prev => ({ ...prev, perfil_id: '', orgao_id: '' }));
        }
      }
    } catch (error) {
      console.error('Erro na verificação de email:', error);
      setError('Erro ao verificar email. Tente novamente.');
      setFieldsDisabled(true);
    } finally {
      setVerifying(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));

    if (name === 'email') {
      if (isEditing) {
        const emailChanged = value.trim().toLowerCase() !== originalEmail.trim().toLowerCase();
        setEmailVerified(!emailChanged);
      } else {
        setEmailVerified(false);
        setFieldsDisabled(true);
        setIsInternalUser(false);
      }

      setUserAlreadyExists(false);
      setAuthTypeMessage('');
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // bloqueios locais
    if (isEditingSelf || isEditingSuperAdmin || userAlreadyExists) return;

    setLoading(true);
    setError(null);
    setValidationErrors({});

    const dataToSend = {
      ...formData,
      is_ldap_user: isInternalUser, // backend decide senha
    };

    try {
      if (isEditing) {
        await api.put(`/users/${id}`, dataToSend);
        navigate('/painel/usuarios');
      } else {
        const response = await api.post('/users', dataToSend);
        const createdUser = response.data?.data ?? response.data ?? {};
        const createdName = createdUser.name ?? formData.name;
        const createdEmail = createdUser.email ?? formData.email;

        navigate('/painel/usuarios', {
          state: {
            userCreationSuccess: {
              name: createdName,
              email: createdEmail,
            },
          },
        });
      }
    } catch (err) {
      if (err.response?.status === 422) {
        setError('Por favor, corrija os erros no formulário.');
        setValidationErrors(err.response.data.errors);
      } else if (err.response?.status === 403) {
        setError('Ação não permitida (403).');
      } else {
        setError(err.response?.data?.message || 'Ocorreu um erro ao salvar o usuário.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 1) Espera verificação de autenticação
  if (authLoading) {
    return (
      <div className="usuarios-form-page">
        <LoadingSpinner text="Verificando permissões..." />
      </div>
    );
  }

  // 2) Loading local da página
  if (loading) {
    return (
      <div className="usuarios-form-page">
        <LoadingSpinner text="Carregando dados do formulário..." />
      </div>
    );
  }

  const normalizedEmail = formData.email.trim().toLowerCase();
  const emailChangedOnEdit = isEditing && normalizedEmail !== originalEmail.trim().toLowerCase();
  const isFormDisabled = isEditingSelf || (isEditingSuperAdmin && user?.id !== 1);
  const perfilDisabled = !isEditing && fieldsDisabled;
  const orgaoDisabled = !isEditing && (fieldsDisabled || isInternalUser);
  const perfilOptions = perfis
    .filter((p) => !isEditingSuperAdmin || Number(p.id) === 1)
    .map((p) => ({ value: String(p.id), label: p.nome }));
  const orgaoOptions = orgaos.map((o) => ({ value: String(o.id), label: o.nome }));

  return (
    <div className="usuarios-form-page">
      <div className="content-header">
        <h1 className="content-title">{pageTitle}</h1>
        <p className="content-subtitle">{pageSubtitle}</p>
      </div>

      {/* Mensagem sobre tipo de autenticação */}
      {authTypeMessage && (
        <div className={`alert ${isInternalUser ? 'alert-info' : 'alert-warning'} mb-3`}>
          <i className={`bi ${isInternalUser ? 'bi-shield-check' : 'bi-exclamation-triangle'} me-2`}></i>
          {authTypeMessage}
        </div>
      )}

      <div className="card usuarios-form-card">
        <div className="card-body">
          <form className="usuarios-form" onSubmit={handleSubmit}>
            {error && <div className="alert alert-danger">{error}</div>}
            {isEditingSelf && (
              <div className="alert alert-warning">Você não pode editar seus próprios dados.</div>
            )}
            {isEditingSuperAdmin && user?.id !== 1 && (
              <div className="alert alert-warning">Você não pode editar os dados do Super Administrador.</div>
            )}

            <fieldset className="usuarios-form-section" disabled={isFormDisabled}>
              <div className="row">
                {/* E-mail com verificação */}
                {!isEditing && (
                  <div className="col-md-6 mb-3">
                    <label htmlFor="email" className="form-label">E-mail</label>
                    <div className="input-group">
                      <input
                        type="email"
                        className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={() => {
                          if (!emailVerified && formData.email && !verifying) {
                            verifyEmail();
                          }
                        }}
                        required
                        autoComplete="email"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-success"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={verifyEmail}
                        disabled={!formData.email || verifying}
                      >
                        {verifying ? 'Verificando...' : 'Verificar'}
                      </button>
                      {validationErrors.email && (
                        <div className="invalid-feedback">{validationErrors.email.join(', ')}</div>
                      )}
                    </div>
                  </div>
                )}

                {isEditing && (
                  <div className="col-md-6 mb-3">
                    <label htmlFor="email" className="form-label">E-mail</label>
                    <div className="input-group">
                      <input
                        type="email"
                        className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={() => {
                          if (emailChangedOnEdit && !verifying) {
                            verifyEmail();
                          }
                        }}
                        required
                        autoComplete="email"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-success"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={verifyEmail}
                        disabled={!emailChangedOnEdit || !formData.email || verifying}
                      >
                        {verifying ? 'Verificando...' : 'Verificar'}
                      </button>
                      {validationErrors.email && (
                        <div className="invalid-feedback">{validationErrors.email.join(', ')}</div>
                      )}
                    </div>
                  </div>
                )}

                <div className="col-md-6 mb-3">
                  <label htmlFor="name" className="form-label">
                    Nome Completo
                    {!isEditing && isInternalUser && <small className="text-muted"> (Obtido do LDAP)</small>}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={!isEditing && (fieldsDisabled || (isInternalUser && formData.name))}
                    required
                    className={`form-control ${validationErrors.name ? 'is-invalid' : ''} ${!isEditing && fieldsDisabled ? 'field-disabled' : ''}`}
                    autoComplete="name"
                  />
                  {validationErrors.name && (
                    <div className="invalid-feedback">{validationErrors.name.join(', ')}</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="perfil_id" className="form-label">
                    Perfil <span className="text-danger">*</span>
                    {!isEditing && isInternalUser && <small className="text-muted"> (Sugestão: Operador)</small>}
                  </label>
                  <AppSelect
                    className={`form-app-select ${validationErrors.perfil_id ? 'is-invalid' : ''} ${perfilDisabled ? 'field-disabled' : ''}`}
                    ariaLabel="Perfil"
                    options={perfilOptions}
                    value={formData.perfil_id}
                    onChange={(value) => handleSelectChange('perfil_id', value)}
                    placeholder="Selecione um perfil..."
                    disabled={perfilDisabled}
                  />
                  {validationErrors.perfil_id && (
                    <div className="invalid-feedback d-block">{validationErrors.perfil_id.join(', ')}</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="orgao_id" className="form-label">
                    Órgão <span className="text-danger">*</span>
                    {!isEditing && isInternalUser && <small className="text-muted"> (SEJUS - obrigatório)</small>}
                  </label>
                  <AppSelect
                    className={`form-app-select ${validationErrors.orgao_id ? 'is-invalid' : ''} ${orgaoDisabled ? 'field-disabled' : ''}`}
                    ariaLabel="Órgão"
                    options={orgaoOptions}
                    value={formData.orgao_id}
                    onChange={(value) => handleSelectChange('orgao_id', value)}
                    placeholder="Selecione um órgão..."
                    disabled={orgaoDisabled}
                  />
                  {validationErrors.orgao_id && (
                    <div className="invalid-feedback d-block">{validationErrors.orgao_id.join(', ')}</div>
                  )}
                </div>
              </div>
            </fieldset>

            <div className="usuarios-form-actions d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => navigate('/painel/usuarios')}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={
                  loading ||
                  isFormDisabled ||
                  (!isEditing && !emailVerified) ||
                  (emailChangedOnEdit && !emailVerified) ||
                  userAlreadyExists
                }
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default UsuariosFormPage;
